import React, { useEffect, useState, useCallback } from 'react';

import { UseOutletContextAdmin } from './Admin';
import { StateLanguage } from '../../../languages/config/StateLanguage';
import { useSubscription } from '../../../contexts/SubscriptionContext';
import { BillingDA } from '../../../services/admin/billing.service';
import { submitSignedForm } from '../../../scripts/payment.form.submit';
import { GlobalSwal } from '../../../styles/swal.style';
import { BillingPaymentMethod } from '../../../models/billing.model';
import AppPreloader from '../../../components/Preloader';

const PM_ARN = 'digital:auth:entity:subscription:*';

const SubscriptionPaymentMethodsPage: React.FunctionComponent = () => {
  const {setRoute, companyForUser} = UseOutletContextAdmin();
  const {lang} = StateLanguage();
  const { snapshot, can: canSub } = useSubscription();
  const [mounted, setMounted] = useState(false);
  const [methods, setMethods] = useState<BillingPaymentMethod[] | null>(null);
  const [methodsError, setMethodsError] = useState(false);
  const [loadIndicator, setLoadIndicator] = useState('off');

  const canManage = canSub('manage', PM_ARN).allowed;

  useEffect(() => {
    setMounted(true);
    setRoute({path: {root: lang.labels.administration, branch: lang.labels.billingPaymentMethods}, company: true});
    return () => setMounted(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const interpolate = (template: string, vars: Record<string, string | number>): string => {
    let result = template;
    for (const [key, value] of Object.entries(vars)) {
      result = result.replace(`{${key}}`, String(value));
    }
    return result;
  };

  const loadMethods = useCallback(async () => {
    if (!companyForUser?.id) return;

    setMethodsError(false);
    try {
      const response = await BillingDA.getPaymentMethods(companyForUser.id);
      if (response.status === 200 && response.data) {
        const data = response.data;
        setMethods(Array.isArray(data) ? data : (data.items ?? data.payment_methods ?? []));
      } else {
        setMethods([]);
      }
    } catch {
      setMethods(null);
      setMethodsError(true);
    }
  }, [companyForUser]);

  useEffect(() => {
    loadMethods();
  }, [loadMethods]);

  const isCollectionAuto = (): boolean => {
    // Check if subscription has automatic collection enabled
    // This may come from the snapshot or from a settings field
    const sub = snapshot?.subscription as any;
    return sub?.collection_mode === 'auto';
  };

  // Server contract: setupPaymentMethod returns { redirect_url, params?, order_id }
  // PostFinance: redirect_url only (GET redirect)
  // myPOS (legacy): redirect_url + params (POST signed form) — see submitSignedForm
  const handleAddMethod = async () => {
    if (!companyForUser?.id) return;

    setLoadIndicator('on');
    try {
      const response = await BillingDA.setupPaymentMethod(companyForUser.id);
      if (response.status === 200 && response.data?.redirect_url) {
        const params = response.data.params
        const hasParams = params && typeof params === 'object' && Object.keys(params).length > 0

        if (hasParams) {
          submitSignedForm(response.data.redirect_url, params);
        } else {
          window.location.assign(response.data.redirect_url);
        }
        return;
      }
      GlobalSwal.fire({
        text: response.data?.message ?? response.message ?? lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
        icon: 'error',
        confirmButtonText: lang.labels.okGotIt,
      });
    } catch {
      GlobalSwal.fire({ text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain, icon: 'error' });
    } finally {
      setLoadIndicator('off');
    }
  };

  const handleDeleteMethod = async (method: BillingPaymentMethod) => {
    // Extra warning if this is the default method and collection is automatic
    const isDefaultAutoWarning = method.is_default && isCollectionAuto();

    const confirmText = isDefaultAutoWarning
      ? lang.labels.billingDeleteDefaultMethodWarning
      : lang.labels.billingDeleteMethodConfirmText;

    const result = await GlobalSwal.fire({
      title: lang.labels.billingDeleteMethodConfirmTitle,
      text: confirmText,
      icon: isDefaultAutoWarning ? 'warning' : 'question',
      showCancelButton: true,
      confirmButtonText: lang.labels.billingDeletePaymentMethod,
      cancelButtonText: lang.labels.cancel,
    });

    if (!result.isConfirmed) return;
    if (!companyForUser?.id) return;

    setLoadIndicator('on');
    try {
      const response = await BillingDA.deletePaymentMethod(companyForUser.id, method._id);
      if (response.status === 200) {
        await loadMethods();
        GlobalSwal.fire({
          title: lang.labels.billingMethodDeleted,
          icon: 'success',
          showConfirmButton: false,
          timer: 2200,
        });
      } else {
        GlobalSwal.fire({
          text: response.data?.message ?? response.message,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt,
        });
      }
    } catch {
      GlobalSwal.fire({ text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain, icon: 'error' });
    } finally {
      setLoadIndicator('off');
    }
  };

  const isExpiringSoon = (method: BillingPaymentMethod): boolean => {
    if (!method.expiry_month || !method.expiry_year) return false;
    const now = new Date();
    const expiry = new Date(method.expiry_year, method.expiry_month); // first of month after expiry
    const thirtyDays = 30 * 24 * 60 * 60 * 1000;
    return (expiry.getTime() - now.getTime()) < thirtyDays;
  };

  const cardBrandDisplay = (brand?: string): string => {
    if (!brand) return '';
    return brand.charAt(0).toUpperCase() + brand.slice(1).toLowerCase();
  };

  if (!mounted) return null;

  return (
    <div className="d-flex flex-column flex-column-fluid">
      <div className="card card-flush mb-5">
        <div className="card-header">
          <div className="card-title">
            <h3 className="fw-bolder">{lang.labels.billingPaymentMethods}</h3>
          </div>
          {canManage && (
            <div className="card-toolbar">
              <button
                className="btn btn-sm btn-primary"
                type="button"
                data-kt-indicator={loadIndicator}
                onClick={handleAddMethod}
              >
                {lang.labels.billingAddPaymentMethod}
              </button>
            </div>
          )}
        </div>
        <div className="card-body pt-0">
          {methodsError ? (
            <div className="text-center py-10">
              <p className="text-gray-600 fs-6 mb-4">{lang.labels.billingUnavailable}</p>
              <button className="btn btn-sm btn-light-primary" type="button" onClick={() => loadMethods()}>
                {lang.labels.retry}
              </button>
            </div>
          ) : methods === null ? (
            <AppPreloader />
          ) : methods.length === 0 ? (
            <div className="text-center py-10 text-gray-500 fs-6">{lang.labels.billingNoPaymentMethods}</div>
          ) : (
            <div className="row g-5">
              {methods.map(method => (
                <div className="col-md-6 col-lg-4" key={method._id}>
                  <div className={`border rounded p-5 ${isExpiringSoon(method) ? 'border-warning' : ''}`}>
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div>
                        <span className="fw-bolder fs-5">{cardBrandDisplay(method.brand)}</span>
                        {method.is_default && (
                          <span className="badge badge-light-primary ms-2 fs-8">{lang.labels.billingCardDefault}</span>
                        )}
                      </div>
                      {isExpiringSoon(method) && (
                        <span className="badge badge-light-warning fs-8">{lang.labels.billingCardExpiringSoon}</span>
                      )}
                    </div>

                    <div className="text-gray-600 fs-6 mb-1">
                      {method.last4 && interpolate(lang.labels.billingCardEnding, { last4: method.last4 })}
                    </div>

                    {method.expiry_month && method.expiry_year && (
                      <div className="text-gray-500 fs-7">
                        {interpolate(lang.labels.billingCardExpires, {
                          month: String(method.expiry_month).padStart(2, '0'),
                          year: method.expiry_year,
                        })}
                      </div>
                    )}

                    {canManage && (
                      <div className="mt-4">
                        <button
                          className="btn btn-sm btn-light-danger"
                          type="button"
                          data-kt-indicator={loadIndicator}
                          onClick={() => handleDeleteMethod(method)}
                        >
                          {lang.labels.billingDeletePaymentMethod}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPaymentMethodsPage;
